import { normalizeMemberStudentNumber } from '../utils/classMemberNumbers';
import { SUPABASE_ANON_KEY, SUPABASE_URL, supabase } from '../supabaseClient';
import { normalizeSeatPosition, type ClassMemberSeatPosition } from '../utils/classMembersStorage';
import { isPersistableUuid } from '../utils/programContentSlideOwner';
import {
  parseCommunicationQuestion,
  type CommunicationQuestion,
} from '../utils/presentationCommunicationAnswers';

export type PresentationCommunicationMember = {
  id: string;
  name: string;
  seatPosition: ClassMemberSeatPosition | null;
  sortOrder: number;
  /** 고정 학생 번호(마이그레이션 전이거나 미지정이면 null → 순서 기반 번호) */
  studentNumber: number | null;
};

export type PresentationCommunicationResponse = {
  memberId: string;
  memberNameSnapshot: string;
  bodyText: string;
  updatedAt: string;
  questionId?: string | null;
};

/** 복수 응답 모드에서 멤버 1명·질문 1개당 최대 제출 횟수(동기화·저장 비용 절감) */
export const PRESENTATION_COMM_MAX_MULTI_RESPONSES = 2;

export type PresentationCommunicationQuestionRow = {
  id: string;
  presentationRefId: string;
  workspaceId: string;
  sortOrder: number;
  promptText: string;
  answerText: string;
  question: CommunicationQuestion | null;
  /** true면 같은 멤버가 최대 2회까지 누적 제출. false면 멤버당 1회만 */
  multiResponse: boolean;
  updatedAt: string;
};

export type PresentationCommunicationBoard = {
  presentationRefId: string;
  workspaceId: string;
  promptText: string;
  gradeId: string | null;
  memberGroupId: string | null;
  questionId: string | null;
  /** 활성 질문 구조(선택형/단답/서술) — 공개 QR 응답 UI 전환용 */
  question: CommunicationQuestion | null;
  /** 재생 중일 때만 true — false면 학생 화면에는 질문이 노출되지 않음(대기 상태) */
  isOpen: boolean;
  multiResponse: boolean;
  members: PresentationCommunicationMember[];
  responses: PresentationCommunicationResponse[];
};

export type PresentationCommunicationRow = {
  presentationRefId: string;
  workspaceId: string;
  gradeId: string | null;
  memberGroupId: string | null;
  promptText: string;
  answerText: string;
  question: CommunicationQuestion | null;
  activeQuestionId: string | null;
  isOpen: boolean;
  updatedAt: string;
};

function formatCommunicationRpcError(message: string): string {
  const msg = String(message ?? '');
  if (/entry number not found/i.test(msg)) return '일치하는 입장번호가 없습니다.';
  if (/entry number ambiguous/i.test(msg)) return '같은 입장번호의 멤버가 여러 명입니다. 교사에게 알려 주세요.';
  if (/invalid entry number/i.test(msg)) return '입장번호는 6자리 숫자여야 합니다.';
  if (/empty response/i.test(msg)) return '답변을 입력해 주세요.';
  if (/response too long/i.test(msg)) return '답변이 너무 깁니다. 짧게 나눠 작성해 주세요.';
  if (/max multi responses reached/i.test(msg)) return '최대 2회까지만 제출할 수 있습니다.';
  if (/already submitted/i.test(msg)) return '이미 응답을 제출했습니다. 단일 응답에서는 한 번만 제출할 수 있습니다.';
  if (/session expired|invalid token/i.test(msg)) return '공개 링크가 만료되었거나 올바르지 않습니다.';
  if (/name required/i.test(msg)) return '이름을 입력해 주세요.';
  if (/name or entry number mismatch/i.test(msg)) return '이름 또는 입장번호가 일치하지 않습니다.';
  if (/too many attempts/i.test(msg)) return '입장 시도가 너무 많아요. 10분 뒤에 다시 시도해 주세요.';
  if (/communication not ready/i.test(msg)) return '아직 문제가 재생되지 않았습니다. 선생님이 재생을 누르면 제출할 수 있어요.';
  if (/grade not set/i.test(msg)) return '클래스(학년) 정보가 없어 입장번호를 확인할 수 없습니다.';
  if (/not authenticated/i.test(msg)) return '로그인이 필요합니다.';
  if (/not a workspace member/i.test(msg)) return '이 워크스페이스에 대한 권한이 없습니다.';
  return msg || '질문 요청에 실패했습니다.';
}

async function postAnonRpc<T>(functionName: string, body: Record<string, unknown>): Promise<T> {
  const baseUrl = SUPABASE_URL.replace(/\/$/, '');
  const anonKey = SUPABASE_ANON_KEY.trim();
  if (!baseUrl || !anonKey) {
    throw new Error('Supabase 환경변수가 설정되지 않았습니다.');
  }
  const res = await fetch(`${baseUrl}/rest/v1/rpc/${functionName}`, {
    method: 'POST',
    headers: {
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });
  const rawText = await res.text();
  let parsed: unknown = null;
  if (rawText) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      parsed = null;
    }
  }
  if (!res.ok) {
    const errBody = parsed as { message?: string } | null;
    throw new Error(formatCommunicationRpcError(errBody?.message || rawText || `HTTP ${res.status}`));
  }
  const okFalse = parsed as { ok?: unknown; error?: unknown } | null;
  if (okFalse && typeof okFalse === 'object' && okFalse.ok === false) {
    throw new Error(formatCommunicationRpcError(String(okFalse.error ?? '')));
  }
  return parsed as T;
}

function parseMember(raw: unknown): PresentationCommunicationMember | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = String(o.id ?? '').trim();
  if (!id) return null;
  const sortOrderNum = Number(o.sort_order ?? o.sortOrder ?? 0);
  return {
    id,
    name: String(o.name ?? ''),
    seatPosition: normalizeSeatPosition(o.seat_position ?? o.seatPosition),
    sortOrder: Number.isFinite(sortOrderNum) ? Math.trunc(sortOrderNum) : 0,
    studentNumber: normalizeMemberStudentNumber(o.student_number ?? o.studentNumber),
  };
}

function parseResponse(raw: unknown): PresentationCommunicationResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const memberId = String(o.member_id ?? o.memberId ?? '').trim();
  if (!memberId) return null;
  return {
    memberId,
    memberNameSnapshot: String(o.member_name_snapshot ?? o.memberNameSnapshot ?? ''),
    bodyText: String(o.body_text ?? o.bodyText ?? ''),
    updatedAt: String(o.updated_at ?? o.updatedAt ?? ''),
    questionId: (() => {
      const q = o.question_id ?? o.questionId;
      if (q == null || String(q).trim() === '') return null;
      return String(q).trim();
    })(),
  };
}

function parseQuestionRow(raw: unknown): PresentationCommunicationQuestionRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = String(o.id ?? '').trim();
  const presentationRefId = String(o.presentation_ref_id ?? o.presentationRefId ?? '').trim();
  if (!id || !presentationRefId) return null;
  const sortOrderNum = Number(o.sort_order ?? o.sortOrder ?? 0);
  return {
    id,
    presentationRefId,
    workspaceId: String(o.workspace_id ?? o.workspaceId ?? ''),
    sortOrder: Number.isFinite(sortOrderNum) ? Math.trunc(sortOrderNum) : 0,
    promptText: String(o.prompt_text ?? o.promptText ?? ''),
    answerText: String(o.answer_text ?? o.answerText ?? ''),
    question: parseCommunicationQuestion(o.question_json ?? o.questionJson),
    multiResponse: Boolean(o.multi_response ?? o.multiResponse ?? false),
    updatedAt: String(o.updated_at ?? o.updatedAt ?? ''),
  };
}

function parseBoard(raw: unknown): PresentationCommunicationBoard | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const presentationRefId = String(o.presentation_ref_id ?? o.presentationRefId ?? '').trim();
  const workspaceId = String(o.workspace_id ?? o.workspaceId ?? '').trim();
  if (!presentationRefId || !workspaceId) return null;
  const membersRaw = Array.isArray(o.members) ? o.members : [];
  const responsesRaw = Array.isArray(o.responses) ? o.responses : [];
  const gradeRaw = o.grade_id ?? o.gradeId;
  const groupRaw = o.member_group_id ?? o.memberGroupId;
  const questionIdRaw = o.question_id ?? o.questionId;
  return {
    presentationRefId,
    workspaceId,
    promptText: String(o.prompt_text ?? o.promptText ?? ''),
    gradeId: gradeRaw == null || String(gradeRaw).trim() === '' ? null : String(gradeRaw).trim(),
    memberGroupId:
      groupRaw == null || String(groupRaw).trim() === '' ? null : String(groupRaw).trim(),
    questionId:
      questionIdRaw == null || String(questionIdRaw).trim() === ''
        ? null
        : String(questionIdRaw).trim(),
    question: parseCommunicationQuestion(o.question_json ?? o.questionJson),
    isOpen: Boolean(o.is_open ?? o.isOpen ?? false),
    multiResponse: Boolean(o.multi_response ?? o.multiResponse ?? false),
    members: membersRaw.map(parseMember).filter((v): v is PresentationCommunicationMember => v != null),
    responses: responsesRaw
      .map(parseResponse)
      .filter((v): v is PresentationCommunicationResponse => v != null),
  };
}

export async function upsertPresentationCommunication(params: {
  presentationRefId: string;
  workspaceId: string;
  promptText: string;
  answerText?: string;
  question?: CommunicationQuestion | null;
  gradeId?: string | null;
  memberGroupId?: string | null;
}): Promise<PresentationCommunicationRow> {
  const presentationRefId = String(params.presentationRefId ?? '').trim();
  const workspaceId = String(params.workspaceId ?? '').trim();
  if (!isPersistableUuid(presentationRefId) || !isPersistableUuid(workspaceId)) {
    throw new Error('프레젠테이션 연결 정보가 올바르지 않습니다.');
  }
  const gradeId = (params.gradeId ?? '').trim();
  const memberGroupId = (params.memberGroupId ?? '').trim();
  const questionJson =
    params.question === undefined ? null : params.question == null ? null : params.question;
  const { data, error } = await supabase.rpc('upsert_presentation_communication', {
    p_presentation_ref_id: presentationRefId,
    p_workspace_id: workspaceId,
    p_prompt_text: params.promptText ?? '',
    p_grade_id: gradeId && isPersistableUuid(gradeId) ? gradeId : null,
    p_member_group_id: memberGroupId || null,
    p_answer_text: params.answerText ?? '',
    p_question_json: questionJson,
  });
  if (error) throw new Error(formatCommunicationRpcError(error.message));
  const row = data as Record<string, unknown> | null;
  if (!row) throw new Error('질문 안내 저장에 실패했습니다.');
  return {
    presentationRefId: String(row.presentation_ref_id ?? presentationRefId),
    workspaceId: String(row.workspace_id ?? workspaceId),
    gradeId: row.grade_id == null ? null : String(row.grade_id),
    memberGroupId: row.member_group_id == null ? null : String(row.member_group_id),
    promptText: String(row.prompt_text ?? ''),
    answerText: String(row.answer_text ?? ''),
    question: parseCommunicationQuestion(row.question_json),
    activeQuestionId: row.active_question_id == null ? null : String(row.active_question_id),
    isOpen: Boolean(row.is_open ?? false),
    updatedAt: String(row.updated_at ?? ''),
  };
}

export async function getPresentationCommunication(params: {
  presentationRefId: string;
  workspaceId: string;
}): Promise<PresentationCommunicationRow | null> {
  const presentationRefId = String(params.presentationRefId ?? '').trim();
  const workspaceId = String(params.workspaceId ?? '').trim();
  if (!presentationRefId || !workspaceId) return null;
  const { data, error } = await supabase
    .from('presentation_communication')
    .select(
      'presentation_ref_id, workspace_id, grade_id, member_group_id, prompt_text, answer_text, question_json, active_question_id, is_open, updated_at'
    )
    .eq('presentation_ref_id', presentationRefId)
    .eq('workspace_id', workspaceId)
    .maybeSingle();
  if (error) throw new Error(formatCommunicationRpcError(error.message));
  if (!data) return null;
  const row = data as Record<string, unknown>;
  return {
    presentationRefId: String(row.presentation_ref_id ?? ''),
    workspaceId: String(row.workspace_id ?? ''),
    gradeId: row.grade_id == null ? null : String(row.grade_id),
    memberGroupId: row.member_group_id == null ? null : String(row.member_group_id),
    promptText: String(row.prompt_text ?? ''),
    answerText: String(row.answer_text ?? ''),
    question: parseCommunicationQuestion(row.question_json),
    activeQuestionId: row.active_question_id == null ? null : String(row.active_question_id),
    isOpen: Boolean(row.is_open ?? false),
    updatedAt: String(row.updated_at ?? ''),
  };
}

export async function listPresentationCommunicationQuestions(params: {
  presentationRefId: string;
  workspaceId: string;
}): Promise<PresentationCommunicationQuestionRow[]> {
  const presentationRefId = String(params.presentationRefId ?? '').trim();
  const workspaceId = String(params.workspaceId ?? '').trim();
  if (!presentationRefId || !workspaceId) return [];
  const { data, error } = await supabase
    .from('presentation_communication_questions')
    .select(
      'id, presentation_ref_id, workspace_id, sort_order, prompt_text, answer_text, question_json, updated_at, created_at'
    )
    .eq('presentation_ref_id', presentationRefId)
    .eq('workspace_id', workspaceId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(formatCommunicationRpcError(error.message));
  return (data ?? [])
    .map(parseQuestionRow)
    .filter((v): v is PresentationCommunicationQuestionRow => v != null);
}

export async function upsertPresentationCommunicationQuestion(params: {
  presentationRefId: string;
  workspaceId: string;
  promptText: string;
  answerText?: string;
  question?: CommunicationQuestion | null;
  questionId?: string | null;
  gradeId?: string | null;
  memberGroupId?: string | null;
}): Promise<PresentationCommunicationQuestionRow> {
  const presentationRefId = String(params.presentationRefId ?? '').trim();
  const workspaceId = String(params.workspaceId ?? '').trim();
  if (!isPersistableUuid(presentationRefId) || !isPersistableUuid(workspaceId)) {
    throw new Error('프레젠테이션 연결 정보가 올바르지 않습니다.');
  }
  const gradeId = (params.gradeId ?? '').trim();
  const memberGroupId = (params.memberGroupId ?? '').trim();
  const questionId = (params.questionId ?? '').trim();
  const questionJson =
    params.question === undefined ? null : params.question == null ? null : params.question;
  const { data, error } = await supabase.rpc('upsert_presentation_communication_question', {
    p_presentation_ref_id: presentationRefId,
    p_workspace_id: workspaceId,
    p_prompt_text: params.promptText ?? '',
    p_answer_text: params.answerText ?? '',
    p_question_json: questionJson,
    p_question_id: questionId && isPersistableUuid(questionId) ? questionId : null,
    p_grade_id: gradeId && isPersistableUuid(gradeId) ? gradeId : null,
    p_member_group_id: memberGroupId || null,
  });
  if (error) throw new Error(formatCommunicationRpcError(error.message));
  const parsed = parseQuestionRow(data);
  if (!parsed) throw new Error('질문 저장에 실패했습니다.');
  return parsed;
}

export async function setPresentationCommunicationActiveQuestion(params: {
  presentationRefId: string;
  workspaceId: string;
  questionId: string;
}): Promise<void> {
  const presentationRefId = String(params.presentationRefId ?? '').trim();
  const workspaceId = String(params.workspaceId ?? '').trim();
  const questionId = String(params.questionId ?? '').trim();
  if (
    !isPersistableUuid(presentationRefId) ||
    !isPersistableUuid(workspaceId) ||
    !isPersistableUuid(questionId)
  ) {
    throw new Error('질문 선택 정보가 올바르지 않습니다.');
  }
  const { error } = await supabase.rpc('set_presentation_communication_active_question', {
    p_presentation_ref_id: presentationRefId,
    p_workspace_id: workspaceId,
    p_question_id: questionId,
  });
  if (error) throw new Error(formatCommunicationRpcError(error.message));
}

/** 재생: 질문을 공개(QR) 화면에 노출하고 단일/복수 응답 모드를 저장 */
export async function playPresentationCommunicationQuestion(params: {
  presentationRefId: string;
  workspaceId: string;
  questionId: string;
  multiResponse?: boolean;
}): Promise<PresentationCommunicationRow> {
  const presentationRefId = String(params.presentationRefId ?? '').trim();
  const workspaceId = String(params.workspaceId ?? '').trim();
  const questionId = String(params.questionId ?? '').trim();
  if (
    !isPersistableUuid(presentationRefId) ||
    !isPersistableUuid(workspaceId) ||
    !isPersistableUuid(questionId)
  ) {
    throw new Error('재생할 질문 정보가 올바르지 않습니다.');
  }
  const { data, error } = await supabase.rpc('play_presentation_communication_question', {
    p_presentation_ref_id: presentationRefId,
    p_workspace_id: workspaceId,
    p_question_id: questionId,
    p_multi_response: params.multiResponse === undefined ? null : params.multiResponse,
  });
  if (error) throw new Error(formatCommunicationRpcError(error.message));
  const row = data as Record<string, unknown> | null;
  if (!row) throw new Error('질문 재생에 실패했습니다.');
  return {
    presentationRefId: String(row.presentation_ref_id ?? presentationRefId),
    workspaceId: String(row.workspace_id ?? workspaceId),
    gradeId: row.grade_id == null ? null : String(row.grade_id),
    memberGroupId: row.member_group_id == null ? null : String(row.member_group_id),
    promptText: String(row.prompt_text ?? ''),
    answerText: String(row.answer_text ?? ''),
    question: parseCommunicationQuestion(row.question_json),
    activeQuestionId: row.active_question_id == null ? null : String(row.active_question_id),
    isOpen: Boolean(row.is_open ?? false),
    updatedAt: String(row.updated_at ?? ''),
  };
}

/** 정지(일시정지): 공개 화면 노출만 끔. active_question_id는 유지 */
export async function stopPresentationCommunication(params: {
  presentationRefId: string;
  workspaceId: string;
}): Promise<void> {
  const presentationRefId = String(params.presentationRefId ?? '').trim();
  const workspaceId = String(params.workspaceId ?? '').trim();
  if (!isPersistableUuid(presentationRefId) || !isPersistableUuid(workspaceId)) {
    throw new Error('프레젠테이션 연결 정보가 올바르지 않습니다.');
  }
  const { error } = await supabase.rpc('stop_presentation_communication', {
    p_presentation_ref_id: presentationRefId,
    p_workspace_id: workspaceId,
  });
  if (error) throw new Error(formatCommunicationRpcError(error.message));
}

export async function deletePresentationCommunicationQuestion(params: {
  presentationRefId: string;
  workspaceId: string;
  questionId: string;
}): Promise<void> {
  const presentationRefId = String(params.presentationRefId ?? '').trim();
  const workspaceId = String(params.workspaceId ?? '').trim();
  const questionId = String(params.questionId ?? '').trim();
  if (
    !isPersistableUuid(presentationRefId) ||
    !isPersistableUuid(workspaceId) ||
    !isPersistableUuid(questionId)
  ) {
    throw new Error('질문 삭제 정보가 올바르지 않습니다.');
  }
  const { error } = await supabase.rpc('delete_presentation_communication_question', {
    p_presentation_ref_id: presentationRefId,
    p_workspace_id: workspaceId,
    p_question_id: questionId,
  });
  if (error) throw new Error(formatCommunicationRpcError(error.message));
}

export async function listPresentationCommunicationResponses(params: {
  presentationRefId: string;
  workspaceId: string;
  questionId?: string | null;
}): Promise<PresentationCommunicationResponse[]> {
  const presentationRefId = String(params.presentationRefId ?? '').trim();
  const workspaceId = String(params.workspaceId ?? '').trim();
  const questionId = (params.questionId ?? '').trim();
  if (!presentationRefId || !workspaceId) return [];
  let query = supabase
    .from('presentation_communication_responses')
    .select('member_id, member_name_snapshot, body_text, updated_at, question_id')
    .eq('presentation_ref_id', presentationRefId)
    .eq('workspace_id', workspaceId);
  if (questionId && isPersistableUuid(questionId)) {
    query = query.eq('question_id', questionId);
  }
  const { data, error } = await query.order('updated_at', { ascending: true });
  if (error) throw new Error(formatCommunicationRpcError(error.message));
  return (data ?? [])
    .map(parseResponse)
    .filter((v): v is PresentationCommunicationResponse => v != null);
}

export function subscribePresentationCommunicationResponses(params: {
  presentationRefId: string;
  workspaceId: string;
  /** 변경된 행의 question_id(알 수 없으면 null) — 캐시 무효화 대상 질문을 좁히는 데 사용 */
  onChange: (questionId: string | null) => void;
}): () => void {
  const presentationRefId = String(params.presentationRefId ?? '').trim();
  const workspaceId = String(params.workspaceId ?? '').trim();
  if (!presentationRefId || !workspaceId) return () => undefined;

  const channel = supabase
    .channel(`presentation-comm-resp:${presentationRefId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'presentation_communication_responses',
        filter: `presentation_ref_id=eq.${presentationRefId}`,
      },
      (payload) => {
        const row = (payload?.new ?? payload?.old ?? null) as Record<string, unknown> | null;
        const qid = row ? String(row.question_id ?? '').trim() : '';
        params.onChange(qid || null);
      }
    )
    .subscribe();

  return () => {
    void supabase.removeChannel(channel);
  };
}

export async function fetchPublicCommunicationByToken(
  token: string
): Promise<PresentationCommunicationBoard | null> {
  const trimmed = String(token ?? '').trim();
  if (!trimmed) return null;
  try {
    const data = await postAnonRpc<unknown>('fetch_public_communication_by_token', {
      p_token: trimmed,
    });
    if (data == null) return null;
    return parseBoard(data);
  } catch (e) {
    throw new Error(formatCommunicationRpcError(e instanceof Error ? e.message : String(e ?? '')));
  }
}

export async function verifyPublicCommunicationEntry(params: {
  token: string;
  memberName: string;
  entryNumber: string;
}): Promise<{
  ok: true;
  memberId: string;
  memberName: string;
  questionId: string | null;
  myResponses: PresentationCommunicationResponse[];
}> {
  try {
    const data = await postAnonRpc<unknown>('verify_public_communication_entry', {
      p_token: String(params.token ?? '').trim(),
      p_member_name: String(params.memberName ?? '').trim(),
      p_entry_number: String(params.entryNumber ?? '').trim(),
    });
    if (!data || typeof data !== 'object') {
      throw new Error('입장 확인에 실패했습니다.');
    }
    const o = data as Record<string, unknown>;
    const memberId = String(o.member_id ?? o.memberId ?? '').trim();
    if (!memberId) throw new Error('입장 확인에 실패했습니다.');
    const questionId = String(o.question_id ?? '').trim();
    const mineRaw = Array.isArray(o.my_responses) ? o.my_responses : [];
    return {
      ok: true,
      memberId,
      memberName: String(o.member_name ?? o.memberName ?? ''),
      questionId: questionId || null,
      myResponses: mineRaw
        .map(parseResponse)
        .filter((v): v is PresentationCommunicationResponse => v != null),
    };
  } catch (e) {
    throw new Error(formatCommunicationRpcError(e instanceof Error ? e.message : String(e ?? '')));
  }
}

export async function submitPublicCommunicationResponse(params: {
  token: string;
  memberName: string;
  entryNumber: string;
  bodyText: string;
}): Promise<PresentationCommunicationResponse> {
  try {
    const data = await postAnonRpc<unknown>('submit_public_communication_response', {
      p_token: String(params.token ?? '').trim(),
      p_member_name: String(params.memberName ?? '').trim(),
      p_entry_number: String(params.entryNumber ?? '').trim(),
      p_body_text: String(params.bodyText ?? ''),
    });
    const parsed = parseResponse(data);
    if (!parsed) throw new Error('응답 저장에 실패했습니다.');
    return parsed;
  } catch (e) {
    throw new Error(formatCommunicationRpcError(e instanceof Error ? e.message : String(e ?? '')));
  }
}

/** 학생 기기 연결(소통방) 응답 1행 — 질문 종료 시 일괄 저장 */
export type CommResponseBatchRow = {
  memberId: string;
  memberName: string;
  /** 방 입장번호로 들어온 학생이 입력한 이름 (명단 입장은 null) */
  nickname: string | null;
  studentNumber: number | null;
  bodyText: string;
  isCorrect: boolean | null;
  answeredAtMs: number;
};

const isMissingFunctionError = (e: { code?: string; message?: string } | null | undefined) =>
  !!e &&
  (e.code === 'PGRST202' ||
    e.code === '42883' ||
    /could not find the function|function .* does not exist/i.test(String(e.message ?? '')));

/**
 * 질문 1개의 학생 기기 응답을 한 번에 저장(같은 질문+학생은 덮어씀 → 다시 저장해도 중복 없음).
 * 1) RPC save_presentation_communication_responses_batch (마이그레이션 20261029100000)
 * 2) RPC 가 아직 없으면 기존 테이블에 삭제 + 일괄 삽입(기본 컬럼만, RLS 그대로)
 */
export async function savePresentationCommunicationResponsesBatch(params: {
  presentationRefId: string;
  workspaceId: string;
  questionId: string;
  rows: CommResponseBatchRow[];
}): Promise<number> {
  const presentationRefId = String(params.presentationRefId ?? '').trim();
  const workspaceId = String(params.workspaceId ?? '').trim();
  const questionId = String(params.questionId ?? '').trim();
  if (!presentationRefId || !workspaceId || !isPersistableUuid(questionId)) throw new Error('저장된 질문이 아니에요.');
  const rows = params.rows.filter((r) => r.memberId.trim() && r.bodyText.trim()).slice(0, 200);
  if (!rows.length) return 0;
  const { data, error } = await supabase.rpc('save_presentation_communication_responses_batch', {
    p_presentation_ref_id: presentationRefId,
    p_workspace_id: workspaceId,
    p_question_id: questionId,
    p_rows: rows.map((r) => ({
      member_id: r.memberId,
      member_name: r.memberName,
      nickname: r.nickname,
      student_number: r.studentNumber,
      body_text: r.bodyText,
      is_correct: r.isCorrect,
      answered_at_ms: r.answeredAtMs,
    })),
  });
  if (!error) return typeof data === 'number' ? data : rows.length;
  if (!isMissingFunctionError(error)) throw new Error(formatCommunicationRpcError(error.message));

  /* 폴백: RPC 미적용 환경 */
  const ids = Array.from(new Set(rows.map((r) => r.memberId)));
  const del = await supabase
    .from('presentation_communication_responses')
    .delete()
    .eq('question_id', questionId)
    .eq('workspace_id', workspaceId)
    .in('member_id', ids);
  if (del.error) throw new Error(formatCommunicationRpcError(del.error.message));
  const seen = new Set<string>();
  const insertRows = rows
    .slice()
    .sort((a, b) => b.answeredAtMs - a.answeredAtMs)
    .filter((r) => (seen.has(r.memberId) ? false : (seen.add(r.memberId), true)))
    .map((r) => ({
      presentation_ref_id: presentationRefId,
      workspace_id: workspaceId,
      question_id: questionId,
      member_id: r.memberId,
      member_name_snapshot: r.memberName,
      body_text: r.bodyText,
      created_at: new Date(r.answeredAtMs || Date.now()).toISOString(),
      updated_at: new Date().toISOString(),
    }));
  const ins = await supabase.from('presentation_communication_responses').insert(insertRows);
  if (ins.error) throw new Error(formatCommunicationRpcError(ins.error.message));
  return insertRows.length;
}
